<script setup lang="ts">
import z from 'zod'
import type { HTMLAttributes } from 'vue'
import type { CekAjaT } from '~/types/cekaja'

const props = withDefaults(
  defineProps<{
    class?: HTMLAttributes['class']
    token?: string
    expand?: boolean
    loading?: boolean
  }>(),
  {
    class: '',
    expand: false,
    token: undefined
  }
)

const cekAjaStore = useCekAjaStore()
const { setForm } = cekAjaStore
const { getForm } = storeToRefs(cekAjaStore)
const originRef = useTemplateRef('originRef')

const state = computed({
  get: () => getForm.value,
  set: (val) => {
    setForm(val)
  }
})

const withVolume = ref(false)
const schema = z.object({
  origin: z.number(),
  destination: z.number(),
  weight: z.number().min(1, 'Berat minimal 1 gram'),
  width: z.number().min(1, 'Lebar minimal 1 cm'),
  height: z.number().min(1, 'Tinggi minimal 1 cm'),
  length: z.number().min(1, 'Panjang minimal 1 cm')
})

const formValidation = computed(() => {
  return schema
    .pick(
      withVolume.value
        ? {
            origin: true,
            destination: true,
            weight: true,
            width: true,
            height: true,
            length: true
          }
        : { origin: true, destination: true, weight: true }
    )
    .safeParse(state.value)
})

const formError = computed(() => {
  if (formValidation.value.success) return null
  return z.treeifyError(formValidation.value.error).properties
})

const emit = defineEmits<{
  (e: 'submit', value: CekAjaT): void
}>()

const onSubmit = () => {
  emit('submit', state.value)
}

onMounted(() => {
  if (originRef.value && props.expand) {
    originRef.value.combobox?.inputRef?.$el?.focus()
  }
})

defineExpose({
  onSubmit
})

watch(withVolume, (newVal) => {
  if (!newVal) {
    state.value.width = undefined
    state.value.height = undefined
    state.value.length = undefined
  }
})
</script>

<template>
  <UiContainer
    :class="
      cn(
        'rounded-xl lg:rounded-2xl bg-background shadow-[0_-1px_6px_-1px_rgba(0,0,0,0.1),0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] dark:bg-surface-container-high',
        props.class,
        {
          'bg-transparent dark:bg-transparent shadow-none p-0 border-0 mx-auto max-w-none w-full xl:w-full':
            props.expand
        }
      )
    "
    :style="{
      viewTransitionName: 'cek-ongkir'
    }"
  >
    <div
      class="flex flex-col items-stretch lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-dashed divide-outline-variant"
    >
      <div class="flex-1">
        <form
          :class="
            cn('flex flex-col justify-between h-full gap-4', {
              'p-5 lg:p-6': !props.expand
            })
          "
          @submit.prevent="onSubmit"
        >
          <div class="flex flex-col gap-4">
            <div v-if="!props.expand" class="flex flex-col gap-1">
              <h2 class="font-bold font-display">Cek Ongkir</h2>
              <p class="text-sm text-current/70">
                Temukan ongkos kirim dari berbagai ekspedisi.
              </p>
            </div>
            <div
              :class="{
                'flex gap-3 items-center': !props.expand
              }"
            >
              <div class="gap-2.5 xl:items-center grow grid lg:grid-cols-5">
                <UiInputControl
                  :label="props.expand ? 'Asal' : undefined"
                  required
                  for="origin"
                  class="flex-1 lg:col-span-2"
                >
                  <UiComboboxAddressSearch
                    id="origin"
                    ref="originRef"
                    v-model="state.origin"
                    v-model:title="state.originTitle"
                    required
                    placeholder="Asal"
                    icon="material-symbols:arrow-upward"
                  />
                  <UiLabelErrorLabel
                    v-if="formError?.origin && state.origin"
                    :text="formError.origin.errors[0]"
                  />
                </UiInputControl>
                <UiInputControl
                  :label="props.expand ? 'Tujuan' : undefined"
                  required
                  for="destination"
                  class="flex-1 lg:col-span-2"
                >
                  <UiComboboxAddressSearch
                    id="destination"
                    v-model="state.destination"
                    v-model:title="state.destinationTitle"
                    required
                    placeholder="Tujuan"
                    icon="material-symbols:arrow-downward"
                  />
                  <UiLabelErrorLabel
                    v-if="formError?.destination && state.destination"
                    :text="formError.destination.errors[0]"
                  />
                </UiInputControl>
                <UiInputControl
                  :label="props.expand ? 'Berat Paket' : undefined"
                  required
                  for="weight"
                  :class="{
                    'xl:max-w-40': !props.expand,
                    'xl:max-w-28': props.expand
                  }"
                >
                  <UiInputFieldRich
                    id="weight"
                    v-model="state.weight"
                    input-mask="numbering"
                    inputmode="numeric"
                    placeholder="Berat"
                    trailing="gram"
                    required
                    @keyup.enter="onSubmit"
                  />
                  <UiLabelErrorLabel
                    v-if="formError?.weight && state.weight"
                    :text="formError.weight.errors[0]"
                  />
                </UiInputControl>
                <template v-if="props.expand">
                  <div class="lg:col-span-5">
                    <UiCheckbox v-model="withVolume" label="Volume Paket" />
                  </div>
                  <div
                    v-if="withVolume"
                    class="grid lg:grid-cols-3 gap-3 lg:col-span-5"
                  >
                    <UiInputControl
                      label="Panjang"
                      required
                      class="flex-1"
                      for="length"
                      :class="{
                        'xl:max-w-40': !props.expand
                      }"
                    >
                      <UiInputFieldRich
                        id="length"
                        v-model="state.length"
                        input-mask="numbering"
                        inputmode="numeric"
                        placeholder="Panjang"
                        trailing="cm"
                        @keyup.enter="onSubmit"
                      />
                      <UiLabelErrorLabel
                        v-if="formError?.length && state.length"
                        :text="formError.length.errors[0]"
                      />
                    </UiInputControl>
                    <UiInputControl
                      label="Lebar"
                      required
                      class="flex-1"
                      for="width"
                      :class="{
                        'xl:max-w-40': !props.expand
                      }"
                    >
                      <UiInputFieldRich
                        id="width"
                        v-model="state.width"
                        input-mask="numbering"
                        inputmode="numeric"
                        placeholder="Lebar"
                        trailing="cm"
                        @keyup.enter="onSubmit"
                      />
                      <UiLabelErrorLabel
                        v-if="formError?.width && state.width"
                        :text="formError.width.errors[0]"
                      />
                    </UiInputControl>
                    <UiInputControl
                      label="Tinggi"
                      required
                      for="height"
                      class="flex-1"
                      :class="{
                        'xl:max-w-40': !props.expand
                      }"
                    >
                      <UiInputFieldRich
                        id="height"
                        v-model="state.height"
                        input-mask="numbering"
                        inputmode="numeric"
                        placeholder="Panjang"
                        trailing="cm"
                        @keyup.enter="onSubmit"
                      />
                      <UiLabelErrorLabel
                        v-if="formError?.height && state.height"
                        :text="formError.height.errors[0]"
                      />
                    </UiInputControl>
                  </div>
                </template>
              </div>
              <UiButton
                :style="{
                  viewTransitionName: 'cek-ongkir-button'
                }"
                type="submit"
                aria-label="Cek Ongkir"
                :loading="props.loading"
                :icon="!props.expand ? 'material-symbols:search' : undefined"
                :disabled="!formValidation.success"
                :class="{
                  'mt-3': props.expand,
                  'rounded-xl': !props.expand
                }"
                :label="props.expand ? 'Cek Ongkir' : undefined"
              />
            </div>
          </div>
        </form>
      </div>
      <slot name="addon"></slot>
    </div>
  </UiContainer>
</template>
